import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  Add,
  Balance,
  Calendar,
  CheckCircle,
  DollarBill,
  InProgressTask,
  MoneyBag,
} from '@factorialco/f0-react/icons/app'
import {
  BudgetsDataCollectionDocument,
  FinanceValueObjectsBudgetableTypeEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { amountWithCurrency } from 'modules/finance/lib/budgets'
import { events } from 'modules/finance/lib/events'

interface Props {
  canManageBudgets: boolean
  canCreateBudgets?: boolean
}

export const BudgetsDataCollection = ({ canManageBudgets, canCreateBudgets }: Props) => {
  const {
    employee: { id: employeeId },
    access: { locale },
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const handleCreateBudget = React.useCallback(() => {
    track(events.clickAddBudget)
    navigate(navigation.expenses.budgets.list.new.fullPath())
  }, [navigate, navigation])

  const source = useGraphqlDataCollectionSource({
    document: BudgetsDataCollectionDocument,
    mapPagination: (data) => {
      return {
        total: data.finance.budgetsConnection.totalCount ?? 0,
        cursor: data.finance.budgetsConnection.pageInfo.endCursor ?? undefined,
        hasNextPage: data.finance.budgetsConnection.pageInfo.hasNextPage ?? false,
      }
    },
    mapData: (data) => data.finance.budgetsConnection.edges.map((edge) => edge.node),
    search: {
      enabled: true,
    },
    presets: [
      {
        label: i18n.t('budgets.list.filters.active'),
        filter: { status: ['active'] },
      },
      {
        label: i18n.t('budgets.list.filters.archived'),
        filter: { status: ['archived'] },
      },
      {
        label: i18n.t('budgets.list.filters.budget_type.trainings'),
        filter: { budgetableBy: ['training_class'] },
      },
      {
        label: i18n.t('budgets.list.filters.budget_type.expenses'),
        filter: { budgetableBy: ['expensable'] },
      },
    ],
    filters: {
      status: {
        label: i18n.t('budgets.list.filters.status'),
        type: 'in',
        hidden: true,
        options: {
          options: [
            {
              label: i18n.t('budgets.list.tabs.active'),
              value: 'active',
            },
            {
              label: i18n.t('budgets.list.tabs.archive'),
              value: 'archived',
            },
          ],
        },
      },
      budgetableBy: {
        label: i18n.t('budgets.list.filters.budgetable_by'),
        type: 'in',
        options: {
          options: [
            {
              label: i18n.t('budgets.list.filters.budget_type.expenses'),
              value: 'expensable',
            },
            {
              label: i18n.t('budgets.list.filters.budget_type.trainings'),
              value: 'training_class',
            },
          ],
        },
      },
    },
    mapVariables: ({ search, filters }) => {
      const statusFilter: string[] = Array.isArray(filters?.status) ? filters.status : []
      const isArchived = statusFilter.includes('archived')
      const isActive = statusFilter.includes('active')
      const showAll = statusFilter.length === 0 || (isArchived && isActive)

      const budgetableByFilter: FinanceValueObjectsBudgetableTypeEnum[] | undefined = Array.isArray(
        filters?.budgetableBy
      )
        ? filters.budgetableBy.filter(
            (value): value is FinanceValueObjectsBudgetableTypeEnum =>
              value === 'expensable' || value === 'training_class'
          )
        : undefined

      return {
        includeInactive: showAll || isArchived,
        includeArchived: showAll || isArchived,
        onlyArchived: isArchived && !isActive,
        ownerOrViewerEmployeeId: canManageBudgets ? undefined : employeeId,
        search,
        budgetableBy: budgetableByFilter,
      }
    },
    primaryActions: canCreateBudgets
      ? () => ({
          label: i18n.t('budgets.navbar.add_budget'),
          icon: Add,
          onClick: handleCreateBudget,
        })
      : undefined,
    itemUrl: (item) => navigation.expenses.budgets.detail.fullPath({ budgetId: item.id }),
  })

  return (
    <OneDataCollection
      id="finance/budgets/v1"
      storage={{
        features: ['filters', 'sortings', 'search'],
      }}
      source={source}
      visualizations={[
        {
          type: 'card',
          options: {
            title: (item) => item.name,
            description: (item) => item.description ?? '',
            cardProperties: [
              {
                label: i18n.t('budgets.list.card.period'),
                icon: Calendar,
                render: (item) => {
                  const formatDate = (date: string | null) => {
                    if (!date) return ''
                    return new Date(date).toLocaleDateString(locale, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })
                  }
                  const from = formatDate(item.effectiveFrom)
                  const until = formatDate(item.effectiveUntil)
                  const dateRange = from && until ? `${from} - ${until}` : from || until || ''
                  return {
                    type: 'text',
                    value: dateRange,
                  }
                },
              },
              {
                label: i18n.t('budgets.list.card.budget_type'),
                icon: CheckCircle,
                render: (item) => {
                  return {
                    type: 'tag',
                    value: {
                      label:
                        item.budgetableBy === 'expensable'
                          ? i18n.t('budgets.detail.budget_type.expensable')
                          : i18n.t('budgets.detail.budget_type.training_class'),
                    },
                  }
                },
              },
              {
                label: i18n.t('budgets.list.card.amount'),
                icon: DollarBill,
                render: (item) => ({
                  type: 'text',
                  value: amountWithCurrency(
                    item.amountCents ? Number(item.amountCents) : 0,
                    item.currency,
                    locale
                  ),
                }),
              },
              {
                label: i18n.t('budgets.list.card.progress'),
                icon: InProgressTask,
                render: (item) => {
                  const spent = amountWithCurrency(
                    item.spentAmountCents ? Number(item.spentAmountCents) : 0,
                    item.currency,
                    locale
                  )
                  const total = amountWithCurrency(
                    item.amountCents ? Number(item.amountCents) : 0,
                    item.currency,
                    locale
                  )
                  const progressValue =
                    item.amountCents && item.amountCents > 0
                      ? (Number(item.spentAmountCents || 0) / Number(item.amountCents)) * 100
                      : 0
                  return {
                    type: 'progressBar',
                    value: {
                      value: progressValue,
                      max: 100,
                      label: `${spent} / ${total}`,
                    },
                  }
                },
              },
              {
                label: i18n.t('budgets.list.card.spent'),
                icon: MoneyBag,
                render: (item) => ({
                  type: 'dotTag',
                  value: {
                    color: 'indigo',
                    label: `${i18n.t('budgets.list.card.spent')}: ${amountWithCurrency(
                      item.spentAmountCents ? Number(item.spentAmountCents) : 0,
                      item.currency,
                      locale
                    )} `,
                  },
                }),
              },
              {
                label: i18n.t('budgets.list.card.available'),
                icon: Balance,
                render: (item) => {
                  return {
                    type: 'dotTag',
                    value: {
                      color: 'lilac',
                      label: `${i18n.t('budgets.list.card.available')}: ${amountWithCurrency(
                        item.availableAmountCents ? Number(item.availableAmountCents) : 0,
                        item.currency,
                        locale
                      )}`,
                    },
                  }
                },
              },
            ],
          },
        },
      ]}
    />
  )
}
